import _ from 'lodash'

import { philosophyLabel } from 'main/util/profile'
import { PageType } from './types'

import * as PageJSON from './PageManifest.json'
import RegionsJSON from './RegionsManifest.json'
import SchoolsJSON from './SchoolsManifest.json'

interface ISchoolForLookup {
  id: number
  name: string
  slug: string
  programType: string | null
  address: {
    city: string
    state: string
  }
  profile: {
    language: string | null
    philosophy: string | null
  }
}

interface ISchoolsManifestEntry {
  id: string
  title: string
  description: string
}

const SchoolsManifest: ISchoolsManifestEntry[] = SchoolsJSON
const RegionsManifest = RegionsJSON
const {
  default: { pageManifest },
} = JSON.parse(JSON.stringify(PageJSON))

export function fetchPageMetatags(page: PageType) {
  return _.get(pageManifest, _.camelCase(page))
}

export function fetchRegionMetatags(region: string) {
  const regionData = _.find(RegionsManifest, { region })

  const { description, title } = regionData
    ? {
        description: regionData.description,
        title: regionData.title,
      }
    : {
        description: descriptionForRegion(region),
        title: titleForRegion(region),
      }

  return {
    description,
    title,
    urlPath: urlPathFromRegion(region),
  }
}

function descriptionForRegion(region: string) {
  return `Wonderschools in ${region} include a mix of preschools, daycares, and other programs designed to foster child development. Browse today.`
}

function titleForRegion(region: string) {
  return `The 10 Best Daycare and Preschool in ${region} | Wonderschool`
}

export function fetchSchoolMetatags(school: ISchoolForLookup) {
  const schoolData = _.find(SchoolsManifest, { id: `${school.id}` })

  const { description, title } = schoolData
    ? {
        description: schoolData.description,
        title: schoolData.title,
      }
    : {
        description: descriptionForSchool(school),
        title: titleForSchool(school),
      }

  return {
    description,
    title,
    urlPath: urlPathForSchool(school),
  }
}

function descriptionForSchool(school: ISchoolForLookup) {
  const {
    name,
    programType,
    profile: { philosophy },
  } = school

  if (philosophy && programType) {
    return `${name} is a ${philosophyLabel(philosophy)} ${_.lowerCase(
      programType,
    )} program in ${cityStateToHuman(school)}. Visit us today to learn more.`
  }

  if (philosophy) {
    return `${name} is a ${philosophyLabel(philosophy)} program in ${cityStateToHuman(
      school,
    )}. Visit us today to learn more.`
  }

  if (programType) {
    return `${name} is a ${_.lowerCase(programType)} program in ${cityStateToHuman(
      school,
    )}. Visit us today to learn more.`
  }

  return `${name} is a program in ${cityStateToHuman(school)}. Visit us today to learn more.`
}

function titleForSchool(school: ISchoolForLookup) {
  const {
    name,
    profile: { philosophy },
  } = school

  if (philosophy) {
    return `${name} | ${philosophyLabel(philosophy)} Child Care in ${cityStateToHuman(school)}`
  }

  return `${name} | Child Care in ${cityStateToHuman(school)}`
}

function cityStateToHuman(school: ISchoolForLookup) {
  const {
    address: { city, state },
  } = school
  return `${_.startCase(city)}, ${_.upperCase(state)}`
}

function urlPathForSchool(school: ISchoolForLookup) {
  const {
    address: { city, state },
    slug,
  } = school
  return _.toLower(`/${_.toLower(state)}/${_.kebabCase(city)}/${slug}`)
}

function urlPathFromRegion(region: string) {
  const regionSplit = _.split(region, ',', 2)
  const city = _.kebabCase(regionSplit[0])
  const state = _.chain(regionSplit[1]).trimStart().toLower().value()

  return _.toLower(`/${state}/${city}`)
}
